import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useToken } from '@/context/useToken';

import LoginDrawer from '@/app/user/LoginDrawer';
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  CardFooter
} from '@/components/ui/card';
import Error from '@/components/common/Error';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import CustomerDisplay from './CustomerDisplay';

import GuestCustomerForm from './GuestCustomerForm';
import VerifyGuestForm from './VerifyGuestForm';

import { useCart, useLinkUserToCart } from '@/data/cart';
import { useSelf } from '@/data/user';
import { useCheckoutStructure } from '@/data/checkout';

const stepKey = 'customer';

const Customer = ({ cartId }) => {
  const cart = useCart(cartId);
  const [apiError, setApiError] = useState('');
  const [verifyGuestEmail, setVerifyGuestEmail] = useState('');
  const { token } = useToken();
  const self = useSelf();
  const linkUserToCart = useLinkUserToCart();
  const checkoutStructure = useCheckoutStructure(cartId);
  const step = checkoutStructure.data.steps.find((s) => s.stepKey === stepKey);

  const handleLoginSuccess = async () => {
    await cart.mutate();
    await checkoutStructure.mutate();
    await self.mutate();
  };

  const handleGuestCustomer = (guestEmail) => {
    setVerifyGuestEmail(guestEmail);
  };

  const handleVerifyGuestSuccess = async () => {
    try {
      const linkedCart = await linkUserToCart(cartId);
      await cart.mutate(linkedCart, false);
      await checkoutStructure.mutate();
      await self.mutate();
    } catch (err) {
      setApiError(err.message);
    }
  };

  useEffect(() => {
    const linkUser = async () => {
      if (token && self.data && cart.data && !cart.data.userId) {
        try {
          const linkedCart = await linkUserToCart(cartId);
          await cart.mutate(linkedCart, false);
          await checkoutStructure.mutate();
        } catch (err) {
          setApiError(err.message);
        }
      }
    };

    linkUser();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!cart.data.userId) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Guest Checkout</CardTitle>
        </CardHeader>
        <CardContent>
          {apiError && <Error error={apiError} />}

          <div className="mb-8">
            {!verifyGuestEmail && (
              <GuestCustomerForm onSuccess={handleGuestCustomer} />
            )}
            {verifyGuestEmail && (
              <VerifyGuestForm
                email={verifyGuestEmail}
                onSuccess={handleVerifyGuestSuccess}
              />
            )}
          </div>

          {!verifyGuestEmail && (
            <>
              <Separator />
              <h3 className="text-center font-medium block pt-4 text-xl">
                Or if you have an account...
              </h3>
              <div className="flex justify-center p-4">
                <LoginDrawer onSuccess={handleLoginSuccess} />
              </div>
            </>
          )}
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Checkout</CardTitle>
      </CardHeader>
      <CardContent>
        <CustomerDisplay key={cart.data.userId} />
      </CardContent>
      <CardFooter className="flex justify-end">
        <Link to={step.nextStepLink}>
          <Button>Continue</Button>
        </Link>
      </CardFooter>
    </Card>
  );
};

export default Customer;
